import { useEffect, useState } from "react";
import { InvalidInputMessage } from "./invalid-input";

export function PinInput({
  length,
  value = "",
  onChange,
  isDisabled = false,
  error,
  className = "",
  enableAutoFocus = true,
}) {
  const [digits, setDigits] = useState(Array(length).fill(""));
  const [currentDigitIndex, setCurrentDigitIndex] = useState(0);

  useEffect(() => {
    if (value && !digits.some((d) => d !== "")) {
      const newDigits = [...digits];
      for (let i = 0; i < Math.min(value.length, length); i++) {
        newDigits[i] = value[i];
      }
      setDigits(newDigits);
    }
  }, [value, digits, length]);

  useEffect(() => {
    if (!enableAutoFocus) return;
    const inputElement = document.getElementById(
      `pin-digit-${currentDigitIndex}`
    );
    if (inputElement) {
      inputElement.focus();
    }
  }, [currentDigitIndex, enableAutoFocus]);

  const handleDigitChange = (index, value) => {
    if (isDisabled) return;

    if (value.length > 1) {
      const inputDigits = value.split("").slice(0, length);
      const newDigits = [...digits];

      inputDigits.forEach((digit, i) => {
        if (i < length) {
          newDigits[i] = digit;
        }
      });

      setDigits(newDigits);

      const nextEmptyIndex = newDigits.findIndex((d) => d === "");
      const newIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
      setCurrentDigitIndex(newIndex);

      onChange(newDigits.join(""));

      return;
    }

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value !== "" && index < length - 1) {
      setCurrentDigitIndex(index + 1);
    } else if (value === "" && index > 0) {
      setCurrentDigitIndex(index - 1);
    }

    onChange(newDigits.join("")); // Call onChange for parent component
  };

  const handlePaste = (e) => {
    e.preventDefault();
    if (isDisabled) return;

    const pastedData = e.clipboardData.getData("text").slice(0, length);

    if (pastedData) {
      const newDigits = [...digits];

      for (let i = 0; i < length; i++) {
        newDigits[i] = i < pastedData.length ? pastedData[i] : newDigits[i];
      }

      setDigits(newDigits);

      const nextEmptyIndex = newDigits.findIndex((d) => d === "");
      setCurrentDigitIndex(nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex);

      onChange(newDigits.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && digits[index] === "" && index > 0) {
      setCurrentDigitIndex(index - 1);
    } else if (e.key === "ArrowLeft" && index > 0) {
      setCurrentDigitIndex(index - 1);
      e.preventDefault();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      setCurrentDigitIndex(index + 1);
      e.preventDefault();
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex justify-center gap-2 md:gap-4">
        {digits.map((digit, index) => (
          <input
            key={index}
            id={`pin-digit-${index}`}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onPaste={(e) => handlePaste(e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={isDisabled}
            autoComplete="one-time-code"
            autoFocus={enableAutoFocus && index === currentDigitIndex}
            className={`w-12 h-12 text-center text-xl border rounded-lg
                text-white bg-background-secondary font-body-primary
              focus:outline-none focus:ring-2 focus:ring-white 
              ${error ? "border-red-500" : "border-gray-300"}`}
          />
        ))}
      </div>

      {error && <InvalidInputMessage errorMessage={error} />}
    </div>
  );
}
